import { SxProps, Theme } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { forwardRef, ReactNode, useEffect, useState } from 'react'
import HintLabel from '../HintLabel'

interface Props {
  value?: unknown
  onChange?: (value: string) => void
  disabled?: boolean
  label?: ReactNode
  labelHint?: ReactNode
  id?: string
  sx?: SxProps<Theme>
  hasLive?: boolean
  hasTesting?: boolean
}

const StatusSwitch = forwardRef((props: Props, _) => {
  const {
    value = 'disabled',
    hasLive = true,
    hasTesting = true,
    onChange,
    label,
    labelHint,
    id,
    disabled,
    sx,
  } = props

  const [selectedValue, setSelectedValue] = useState(value || 'disabled')

  useEffect(() => setSelectedValue(value), [value])

  const handleChange = (_, value) => {
    if (!value) return

    setSelectedValue(value)
    onChange?.(value)
  }

  return (
    <Box sx={sx}>
      <HintLabel hint={labelHint}>{label}</HintLabel>

      <Stack direction="row" spacing={4}>
        <ToggleButtonGroup
          value={selectedValue}
          disabled={disabled}
          size="small"
          exclusive
          id={id}
          onChange={handleChange}
          aria-label="status switch"
        >
          {
            <ToggleButton
              value="disabled"
              color="secondary"
              aria-label="disabled"
              data-cy="status_disabled"
            >
              Inactive
            </ToggleButton>
          }
          {hasTesting && (
            <ToggleButton value="test" color="warning" aria-label="testing">
              Testing
            </ToggleButton>
          )}

          {hasLive && (
            <ToggleButton value="enabled" color="success" aria-label="enabled">
              Active
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </Stack>
    </Box>
  )
})

export default StatusSwitch
